/**
 * How items show in the UI's item cards and grids. React-free so the game
 * worker maps in-run carried items with the same full details (base stats,
 * requirements, essence value) Character Home shows.
 */
import { isEmpyrealCrystalItem, resolveGearPotency, resolveGearRequirement } from '@sorrem/shared';
import type { CarriedItem, CharacterDisciplines, Equipment, GearRequirementSource } from '@sorrem/shared';
import type { UIInventoryItem } from '@sorrem-client/types';
import { toUICrystalItem } from './crystal-item';

/** The UI item category an equipment type groups under. */
function toInventoryItemType(equipmentType: Equipment['equipmentType']): UIInventoryItem['type'] {
    if (equipmentType === 'armor') {
        return 'armor';
    }

    if (equipmentType === 'ring' || equipmentType === 'amulet') {
        return 'accessory';
    }

    return 'weapon';
}

/** An item's requirement (#250), when it asks for one. */
function toUIGearRequirement(item: GearRequirementSource): Pick<UIInventoryItem, 'requirement'> {
    const requirement = resolveGearRequirement(item);
    return requirement.percent > 0 ? { requirement: { discipline: requirement.discipline, percent: requirement.percent } } : {};
}

/** A potency below full strength, else nothing: an item card only notes a weakened item. */
function toUIPotency(potency: number): Pick<UIInventoryItem, 'potency'> {
    return potency < 1 ? { potency } : {};
}

/**
 * A piece of equipment as the UI's item cards and grids show it. An item
 * as wielded keeps the potency it comes at; a carried or stored one (at full
 * strength) is judged against `disciplines`, the character who would wear
 * it, so its card says it would be weakened before it is equipped (#250).
 */
export function toUIInventoryItem(item: Equipment, disciplines?: CharacterDisciplines): UIInventoryItem {
    const potency = item.potency ?? (disciplines ? resolveGearPotency(item, disciplines) : 1);
    return {
        id: item.id,
        itemDefinitionId: item.itemDefinitionId,
        ...(item.modelId ? { modelId: item.modelId } : {}),
        ...(item.offHandModelId ? { offHandModelId: item.offHandModelId } : {}),
        name: item.name,
        type: toInventoryItemType(item.equipmentType),
        equipmentType: item.equipmentType,
        bonuses: item.bonuses,
        ...(item.combat ? { combat: item.combat } : {}),
        rarity: item.instance?.rarity ?? 'common',
        ...(item.instance ? { instance: item.instance } : {}),
        ...(item.baseStats ? { baseStats: item.baseStats } : {}),
        qty: 1,
        essenceValue: item.essenceValue,
        ...toUIGearRequirement(item),
        ...toUIPotency(potency),
    };
}

/**
 * A carried or stored item card judged again for the character who would
 * wear it (#250): its requirement, and its potency for them, so a card that
 * moved or a character whose disciplines changed reads right. Its potency is
 * kept with no wearer to judge by; an item that isn't equipment is unchanged.
 */
export function withGearPotency(item: UIInventoryItem, disciplines: CharacterDisciplines | undefined): UIInventoryItem {
    if (item.equipmentType === undefined) {
        return item;
    }
    const { potency: previous, requirement: _requirement, ...rest } = item;
    const source: GearRequirementSource = { equipmentType: item.equipmentType, ...(item.instance ? { instance: item.instance } : {}) };
    const potency = disciplines ? resolveGearPotency(source, disciplines) : previous ?? 1;
    return { ...rest, ...toUIGearRequirement(source), ...toUIPotency(potency) };
}

/** The equipment type of an item card that has none (an older payload): by its category. */
function toEquipmentType(item: UIInventoryItem): Equipment['equipmentType'] {
    if (item.equipmentType !== undefined) {
        return item.equipmentType;
    }
    if (item.type === 'armor') {
        return 'armor';
    }
    return item.type === 'accessory' ? 'ring' : 'melee';
}

/**
 * The equipment an item card shows: the inverse of `toUIInventoryItem`, for
 * the shared rules that read equipment (moves, crafting, comparisons).
 */
export function toEquipment(item: UIInventoryItem): Equipment {
    return {
        id: item.id,
        itemDefinitionId: item.itemDefinitionId,
        ...(item.modelId ? { modelId: item.modelId } : {}),
        ...(item.offHandModelId ? { offHandModelId: item.offHandModelId } : {}),
        name: item.name,
        icon: '',
        equipmentType: toEquipmentType(item),
        bonuses: item.bonuses ?? {},
        essenceValue: item.essenceValue ?? {},
        ...(item.combat ? { combat: item.combat } : {}),
        ...(item.instance ? { instance: item.instance } : {}),
        ...(item.baseStats ? { baseStats: item.baseStats } : {}),
    };
}

/**
 * A carried slot's item as the in-run inventory shows it: a crystal stack, a
 * piece of equipment (judged against `disciplines`, see `toUIInventoryItem`),
 * or `undefined` for an empty slot.
 */
export function toUICarriedItem(item: CarriedItem | undefined, disciplines?: CharacterDisciplines): UIInventoryItem | undefined {
    if (isEmpyrealCrystalItem(item)) {
        return toUICrystalItem(item);
    }
    if (!item) {
        return undefined;
    }
    return toUIInventoryItem(item, disciplines);
}
